import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Avatar, Badge, Button, Tooltip } from 'antd';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { LuChevronsLeftRight } from 'react-icons/lu';
import { FaPhoneAlt, FaFlag } from 'react-icons/fa';
import { MdVideocam } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import * as actions from '@actions';
import { breakpoints } from '@lib/breakpoints';
import { useRuntimeConfig } from 'vl-core/useRuntimeConfig';
import useMediaQuery from 'vl-common/src/hooks/useMediaQuery';
import useAvatar from 'vl-common/src/hooks/useAvatar';
import { useRoom } from '@hooks/useRoom';
import { useTwilioWS } from '@hooks/twilioWS';
import usePatientStatusPanel from '@hooks/usePatientStatusPanel';
import useCallStatus from '@hooks/useCallStatus';
import useCallStatusUpdaters from '@hooks/useCallStatusUpdaters';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { Token } from '@hooks/video/useChimeVideoCall';
import CallMenu from '@components/Clinicians/Session/Components/CallMenu';
import SessionPatientSummary from '@components/Clinicians/Session/Components/SessionPatientSummary';
import { SimpleVideo } from '@components/Video/SimpleVideo';
import VideoContainer from '@components/Video/VideoContainer';
import useUserNextAppointments from '@hooks/useUserNextAppointments';
import { useStopTwilioOnUnmount } from '@hooks/useStopTwilioOnUnmount';
import useVoiceCalling from '@hooks/useVoiceCalling';
import useUserAppointments from '@hooks/useUserAppointments';
import { useRouter } from 'next/router';
import useAuth from '@vl-core/useAuth';
import useAudio from '@hooks/useAudio';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import ReportCallIssueModal from '@components/Modal/ReportCallIssueModal';
import { usePatientInfo } from '@components/Clinicians/Session/hooks/usePatientInfo';
import CallPatient from '../CallPatient';
import { AudioDevicesModal } from './Components/AudioDevicesModal';
import { Lobby } from '@lib/Lobby';
import { useVideoCallSignallingType } from '@hooks/useVideoCallSignallingType';
import { useTelValidation } from '@hooks/useTelValidation';
import { confirmationModal, errorModal } from 'vl-common/src/components/Modals';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { AITranscribeControlPanel } from '@components/Clinicians/Session/Components/AITranscribeControlPanel';
import HeidiAiIcon from '@components/Clinicians/Session/Components/HeidiAiIcon';
import { useHeidiEnabled } from '@hooks/useHeidiEnabled';
import { useHeidiSession } from '@components/Clinicians/Session/hooks/useHeidiSession';
import { useDPACheck } from '@hooks/useDPACheck';
import { AITranscribeAlert } from '@components/Clinicians/Session/Components/AITranscribeAlert';
import { audioTranscriptionService } from '@lib/audioTranscriptionService';
import AppointmentEndNotification from '@components/AppointmentEndNotification/AppointmentEndNotification';

const MAX_WIDTH = breakpoints.md;
const MIN_WIDTH = breakpoints.md + 1;

export const PatientStatusPanelStyled = styled.div`
  @media (max-width: ${MAX_WIDTH}px) {
    position: relative;
  }

  @media (min-width: ${MIN_WIDTH}px) {
    position: sticky;
    top: 0;
    height: calc(100vh - 6rem);
  }

  @media (min-width: ${MIN_WIDTH}px) {
    position: sticky;
    top: 0;
    height: 100vh;
  }

  & > div {
    background: #fff;
    z-index: 100;
    overflow: hidden;

    -webkit-transition:
      min-width 0.5s ease-in-out,
      width 0.5s ease-in-out,
      height 0.5s ease-in-out;
    -moz-transition:
      min-width 0.5s ease-in-out,
      width 0.5s ease-in-out,
      height 0.5s ease-in-out;
    -o-transition:
      min-width 0.5s ease-in-out,
      width 0.5s ease-in-out,
      height 0.5s ease-in-out;
    transition:
      min-width 0.5s ease-in-out,
      width 0.5s ease-in-out,
      height 0.5s ease-in-out;

    @media (max-width: ${MAX_WIDTH}px) {
      position: relative;
      width: 100vw;
      border-bottom: 0.5px solid #868686;
      z-index: -1;
    }

    @media (min-width: ${MIN_WIDTH}px) {
      position: relative;
      z-index: -1;
      height: 100%;
    }
  }

  .aws_container {
    width: 100%;
    height: 100%;
    min-height: 400px;
    background: white;
    box-shadow: 0 3px 6px #0000004d;
    border-radius: 10px;
    position: relative;
    margin-top: 20px;
    padding: 10px;
  }
`;

export const ExpandedContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: auto;

  @media (max-width: ${MAX_WIDTH}px) {
    height: 70vh;
    gap: 0.5rem;
    padding: 1rem;
  }

  @media (min-width: ${MIN_WIDTH}px) {
    width: 30vw;
    min-width: 30vw;
    height: 100%;
    gap: 1rem;
    padding: 5vw 2rem 8rem 2rem;
  }
`;

const CollapsedContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  height: 100%;

  @media (min-width: ${MIN_WIDTH}px) {
    padding: 1rem 1rem 8rem 1rem;
  }

  @media (max-width: ${MAX_WIDTH}px) {
    height: 100px;
    flex-direction: row;
  }
`;

const StyledCollapseButton = styled(Button)`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  scale: 0.7;
  border-radius: 50%;
  border-width: 0.5px;
  border-color: #868686;
  color: #868686;
  z-index: 100;

  svg {
    transform: scaleY(1.2);

    path {
      stroke-width: 1;
    }
  }

  @media (max-width: ${MAX_WIDTH}px) {
    right: 20px;
    bottom: -16px;

    svg {
      transform: rotate(90deg);
    }
  }

  @media (min-width: ${MIN_WIDTH}px) {
    top: 20px;
    right: -21px;
  }
`;

const StyledQuickCallButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border-width: 1px;
  border-color: #868686;
  color: #868686;
  z-index: 100;
`;

export const StyledPatientAvatar = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  height: 90px;
  width: 90px;
  overflow: hidden;

  @media (max-width: ${MAX_WIDTH}px) {
    width: 90px;
    min-width: 40px;
    height: 90px;
    min-height: 40px;
  }

  span {
    width: 100%;
    height: 100%;
  }

  img {
    display: inline;
    height: 100%;
    width: auto;
    object-fit: cover;
  }
`;

export const StyledPatientName = styled.div`
  font-size: 24px;
  letter-spacing: 0;
  color: var(--black);
  opacity: 1;
  text-align: center;

  @media (max-width: ${MAX_WIDTH}px) {
    font-size: 18px;
  }
`;

const StyledPatientPrefName = styled.div`
  font-size: 18px;
  text-align: center;

  @media (max-width: ${MAX_WIDTH}px) {
    font-size: 14px;
  }
`;

enum TemplateCode {
  CaseReview = 'PAT-MISSED-CALL-CASE-REVIEW',
  Appointment = 'PAT-MISSED-CALL'
}

const PatientStatusPanel = ({ onExpandedChanged = (val: boolean) => {} }) => {
  const dispatch = useDispatch();
  const appointment_id = useSessionAppointmentId();
  const isMobileView = useMediaQuery(`(max-width: ${MAX_WIDTH}px)`);
  const { PVIDEO } = useRuntimeConfig();
  const { t } = useTranslation('common');
  const [, invalidateAppointments] = useUserNextAppointments(10);
  const {
    isCallWindowHidden,
    isCallWindowCollapsed,
    isCallWindowExpanded,
    canCallWindowBeCollapsed,
    isVideoSessionType,
    showCallMenu,
    isCollapseButtonDisabled,
    isQuickVideoCallButtonDisabled,
    isQuickAudioCallButtonDisabled,
    onSwitchCallWindow
  } = usePatientStatusPanel();

  useEffect(() => {
    onExpandedChanged(isCallWindowExpanded);
  }, [isCallWindowExpanded]);

  const [, setAlert] = useSessionFormState('alert');
  const [devicesModalOpen, setDevicesModalOpen] = useState(false);

  const [showCallProblemModal, setShowCallProblemModal] = useState(false);

  const [audioCallClicked, setAudioCallClicked] = useState(false);

  const { appointment } = useSessionAppointment();
  const { makeDPACheckRequired } = useDPACheck();
  const { clinician_user_guid, patient_user_guid, appointment_completed: isCompleted, case_review } = appointment;

  const callStatus = useCallStatus(appointment);

  const avatar = useAvatar(patient_user_guid);
  const { data: patientInfo } = usePatientInfo();
  const { startCall, dial, endCall, stopCalling, updatersReady } = useCallStatusUpdaters(appointment);

  const { query } = useRouter();

  const { user } = useAuth();
  const [, playVideoRinger, stopVideoRinger] = useAudio('/static/telephone-ring.mp3', 0.6);

  const { refetchUserAppointments: refresh } = useUserAppointments(
    'USER_APPOINTMENTS',
    { appt_id: appointment_id, team: !!query.team },
    { refetchOnWindowFocus: false, enabled: !!user && !!query.req }
  );

  const { isPrimaryTelValid } = useTelValidation(appointment);
  const showAiNotes = useHeidiEnabled(appointment_id);

  const { sendCloseRoomRequest } = useTwilioWS(appointment, {
    onCloseRoom: useCallback((msg: { closeRoomError: boolean }) => msg?.closeRoomError && handleCloseRoomError(), [])
  });

  useEffect(() => {
    if (callStatus === 'in-progress' || callStatus !== 'calling') {
      stopVideoRinger();
    } else if (callStatus === 'calling') {
      playVideoRinger();
    }

    return () => {
      stopVideoRinger();
    };
  }, [callStatus, stopVideoRinger, playVideoRinger]);

  const {
    onCallPatient,
    onClickHangupCall,
    audioCallStatus,
    audioCallStatusText,
    audioCallUI,
    isAudioCallStarted,
    callPatientProps,
    currentInput,
    currentOutput,
    updateInputDevice,
    updateOutputDevice
  } = useVoiceCalling(appointment, stopVideoRinger, refresh);

  const { startSession, status, pause, hasHeidiError } = useHeidiSession();

  const STARTED_CALL_STATES = ['in-progress', 'ringing', 'completed'];

  const hasCallBeenStarted =
    STARTED_CALL_STATES.includes(callStatus) ||
    STARTED_CALL_STATES.includes(audioCallStatus) ||
    status !== 'idle' ||
    Boolean(audioCallUI || isAudioCallStarted || isCompleted);

  const showOriginalCallMenu =
    showCallMenu && (!showAiNotes || hasHeidiError || appointment.case_review || hasCallBeenStarted);

  const showAICallMenu = !appointment.case_review && !appointment.audit_appt_id && showCallMenu && !hasCallBeenStarted;

  const reminderAlreadySent =
    sessionStorage.getItem(`${appointment_id}-reminder-sent`) === appointment.datetime.toString() ||
    sessionStorage.getItem(`${appointment_id}-reminder-sent`) === '1';

  const [sendReminderInProgress, setSendReminderInProgress] = useState(false);
  const [sendReminderDisabled, setSendReminderDisabled] = useState(
    callStatus === 'in-progress' ||
      callStatus === 'calling' ||
      reminderAlreadySent ||
      ((audioCallStatus === 'in-progress' || audioCallStatus === 'initiated' || audioCallStatus === 'ringing') &&
        audioCallUI)
  );

  const sendReminderHidden =
    callStatus === 'completed' ||
    appointment.audit_appt_id ||
    !showCallMenu ||
    (sessionStorage.getItem(`${appointment_id}-call-attempted`) !== appointment?.datetime.toString() &&
      sessionStorage.getItem(`${appointment_id}-call-attempted`) !== '1');

  useEffect(() => {
    setSendReminderDisabled(
      callStatus === 'in-progress' ||
        callStatus === 'calling' ||
        reminderAlreadySent ||
        ((audioCallStatus === 'in-progress' || audioCallStatus === 'initiated' || audioCallStatus === 'ringing') &&
          audioCallUI)
    );
  }, [callStatus, reminderAlreadySent, audioCallStatus, audioCallUI]);

  const [secondaryToken, setSecondaryToken] = useState('');

  const callStatusRef = useRef(callStatus);
  const dialRef = useRef(dial);

  useEffect(() => {
    callStatusRef.current = callStatus;
    dialRef.current = dial;
  }, [callStatus, dial]);

  const onPatientControlMessage = useCallback((msg: { iam?: string }) => {
    if (msg?.iam === 'patient' && callStatusRef.current === 'calling') {
      dialRef.current();
    }
  }, []);

  const { roomName, token, sendTokenRequest, open, sendControlMessage } = useTwilioWS(appointment, {
    onTokenReceived: (token) => setSecondaryToken(token),
    onControlMessage: onPatientControlMessage
  });
  const { room, error: connectionError } = useRoom(
    roomName,
    (isVideoSessionType && !isCompleted && token) || '',
    appointment
  );
  const lobby = room ? Lobby.forRoom(room) : undefined;
  const signallingType = useVideoCallSignallingType(appointment);

  useStopTwilioOnUnmount(room);

  useEffect(() => {
    audioTranscriptionService.setVideoRoom(isVideoSessionType ? (room ?? null) : null);
  }, [isVideoSessionType, room]);

  useEffect(() => {
    return () => {
      audioTranscriptionService.setVideoRoom(null);
    };
  }, []);

  useEffect(() => {
    if (!open) return () => {};

    sendControlMessage({ partyStatus: 'in-session', iam: 'clinician' });

    return () => sendControlMessage({ partyStatus: 'connected', iam: 'clinician' });
  }, [sendControlMessage, open]);

  useEffect(() => {
    if (connectionError) {
      setAlert({
        type: 'error',
        message: connectionError?.toString()
      });
    } else {
      setAlert(null);
    }
  }, [connectionError]);

  const isCallActive = callStatus === 'in-progress' || audioCallStatus === 'in-progress' || isAudioCallStarted;

  useEffect(() => {
    const shouldStartSession = status === 'idle' && !appointment.case_review && isCallActive;
    if (shouldStartSession) {
      startSession().then();
    }
  }, [status, appointment.case_review, startSession, isCallActive]);

  const setCallAttempted = () => {
    sessionStorage.setItem(`${appointment_id}-call-attempted`, appointment?.datetime?.toString() || '1');
  };

  const setReminderSent = () => {
    sessionStorage.setItem(`${appointment_id}-reminder-sent`, appointment?.datetime?.toString() || '1');
  };

  useEffect(() => {
    if (!updatersReady) return;

    switch (callStatus) {
      case 'completed':
        invalidateAppointments();
        break;

      case 'in-progress':
        // it's the patient that "accepts" the clinician's call, they send an in-progress status change
        // but the CASTART event is sent by the clinician's startCall function.
        startCall();
        setCallAttempted();
        break;

      case 'calling':
        setCallAttempted();
        break;
      default:
        break;
    }
  }, [callStatus, invalidateAppointments, updatersReady, startCall]);

  useEffect(() => {
    if (!audioCallUI && devicesModalOpen) {
      setDevicesModalOpen(false);
    }
    if (audioCallUI) {
      setCallAttempted();
    }
  }, [audioCallUI, devicesModalOpen]);

  const onStartVideoCall = () => {
    dial().then(() => {
      dispatch(actions.sendPushNotification(clinician_user_guid, appointment_id));
      makeDPACheckRequired();
    });
  };

  const handleCloseRoomError = () => {
    errorModal({
      title: t('Error ending video call'),
      content: t('Unable to close room. Room is already closed or does not exist.')
    });
  };

  if (isCallWindowHidden) return null;

  return (
    <div>
      <AppointmentEndNotification appointment={appointment} audioCallStatus={audioCallStatus} />
      <PatientStatusPanelStyled>
        {canCallWindowBeCollapsed && (
          <StyledCollapseButton
            data-testid="switch-call-window"
            onClick={() => onSwitchCallWindow()}
            icon={<LuChevronsLeftRight size={24} />}
            disabled={isCollapseButtonDisabled}
          />
        )}
        <div
          css={css`
            @media (max-width: ${MAX_WIDTH}px) {
              height: ${isCallWindowCollapsed ? '100px' : '70vh'};
            }

            @media (min-width: ${MIN_WIDTH}px) {
              min-width: ${isCallWindowCollapsed ? '100px' : '30vw'};
              width: ${isCallWindowCollapsed ? '100px' : '30vw'};
            }
          `}
        >
          {isCallWindowCollapsed ? (
            <CollapsedContent style={{ position: 'relative' }}>
              {showAiNotes && (
                <div style={{ position: 'absolute', top: 70 }}>
                  <HeidiAiIcon expandPanel={onSwitchCallWindow} />
                </div>
              )}
              {!appointment.audit_appt_id && isVideoSessionType && (
                <Tooltip
                  placement={isMobileView ? 'bottom' : 'right'}
                  title={callStatus === 'calling' ? t('Click to stop calling') : t('Click to video call')}
                  trigger={['hover', 'focus']}
                >
                  <StyledQuickCallButton
                    data-testid="quick-video-call-patient"
                    onClick={async () => {
                      if (callStatus === 'calling') {
                        await stopCalling();
                        return;
                      }
                      onStartVideoCall();
                    }}
                    icon={<MdVideocam size={32} />}
                    danger={callStatus === 'calling'}
                    disabled={isQuickVideoCallButtonDisabled}
                  />
                </Tooltip>
              )}
              {!appointment.audit_appt_id && (
                <Tooltip
                  placement={isMobileView ? 'bottom' : 'right'}
                  title={audioCallUI ? t('Click to stop calling') : t('Click to audio call')}
                  trigger={['hover', 'focus']}
                >
                  <Badge
                    count={
                      isPrimaryTelValid ? null : (
                        <ExclamationCircleFilled
                          style={{
                            color: '#faad14',
                            fontSize: '14px',
                            backgroundColor: '#fff',
                            borderRadius: '50%',
                            zIndex: 101
                          }}
                        />
                      )
                    }
                    offset={[-5, 5]}
                  >
                    <StyledQuickCallButton
                      data-testid="quick-audio-call-patient"
                      onClick={async () => {
                        if (audioCallClicked) return;

                        if (audioCallUI) {
                          onClickHangupCall();
                          return;
                        }
                        const triggerCall = async () => {
                          try {
                            setAudioCallClicked(true);
                            await onCallPatient(appointment, true);
                          } catch (error) {
                            console.error('Call failed', error);
                          } finally {
                            setAudioCallClicked(false);
                          }
                        };

                        if (isPrimaryTelValid) {
                          await triggerCall();
                        } else {
                          confirmationModal({
                            title: 'Number may be invalid',
                            content: `The number ${appointment?.patient_tel_mobile_no} may be invalid`,
                            okText: 'Call Anyway',
                            cancelText: 'Cancel',
                            onOk: () => onCallPatient(appointment, true)
                          });
                        }
                      }}
                      icon={<FaPhoneAlt size={26} />}
                      danger={audioCallUI}
                      css={css`
                        transform: rotate(${audioCallUI ? '135deg' : '0'});
                      `}
                      loading={audioCallClicked}
                      disabled={isQuickAudioCallButtonDisabled || audioCallClicked}
                    />
                  </Badge>
                </Tooltip>
              )}
              <Tooltip
                placement={isMobileView ? 'bottom' : 'right'}
                title={showAiNotes ? 'Click to provide feedback' : 'Click to report a call issue'}
                trigger={['hover', 'focus']}
              >
                <StyledQuickCallButton
                  data-testid="report-call-issue"
                  onClick={() => {
                    setShowCallProblemModal(true);
                  }}
                  icon={<FaFlag size={26} />}
                  disabled={isQuickAudioCallButtonDisabled}
                />
              </Tooltip>
            </CollapsedContent>
          ) : (
            <ExpandedContent>
              {isVideoSessionType && callStatus === 'in-progress' ? (
                <>
                  {PVIDEO === 'twilio' && (room || signallingType === 'ws') && (
                    <SimpleVideo
                      appointment={appointment}
                      room={room!}
                      secondaryToken={secondaryToken}
                      sendTokenRequest={sendTokenRequest}
                      open={open}
                      onRequestHangup={() => {
                        pause();
                        endCall().then();
                        sendCloseRoomRequest();
                      }}
                      roomName={room?.name || roomName}
                    />
                  )}
                  {PVIDEO === 'chime' && (
                    <div className="aws_container">
                      <VideoContainer
                        token={token as Token}
                        clinician_guid={clinician_user_guid}
                        patient_guid={patient_user_guid}
                        callStatus={callStatus}
                        isClinician
                        sendTokenRequest={sendTokenRequest}
                        open={open}
                        endCall={endCall}
                        appointment={appointment}
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <StyledPatientAvatar>
                    <Avatar src={avatar} />
                  </StyledPatientAvatar>
                  <StyledPatientName>
                    {appointment.patient}
                    {patientInfo?.pref_name && (
                      <StyledPatientPrefName>
                        {`${t('Preferred Name')}: ${patientInfo.pref_name}`}
                      </StyledPatientPrefName>
                    )}
                  </StyledPatientName>
                  {isVideoSessionType && <SessionPatientSummary callText={audioCallStatusText} lobby={lobby} />}
                </>
              )}
              {showAiNotes && <AITranscribeAlert />}

              {!appointment.audit_appt_id && showOriginalCallMenu && (
                <CallMenu onCallPatient={onCallPatient} audioCallUI={audioCallUI} />
              )}

              {showAiNotes && !hasHeidiError && (
                <AITranscribeControlPanel isCaseReview={appointment.case_review}>
                  {showAICallMenu && <CallMenu onCallPatient={onCallPatient} audioCallUI={audioCallUI} />}
                </AITranscribeControlPanel>
              )}

              {!sendReminderHidden && (
                <Button
                  data-testid="send-reminder-button"
                  onClick={async () => {
                    const template = case_review ? TemplateCode.CaseReview : TemplateCode.Appointment;

                    setSendReminderInProgress(true);
                    const res = await dispatch(
                      actions.sendReminderMessage(appointment?.clinician_user_guid, appointment_id, template)
                    );

                    setSendReminderInProgress(false);
                    if (res) {
                      setReminderSent();
                    }
                  }}
                  disabled={sendReminderDisabled || sendReminderInProgress}
                  loading={sendReminderInProgress}
                >
                  {reminderAlreadySent ? t('Reminder sent') : t('Send reminder')}
                </Button>
              )}
              {showCallMenu && (
                <Button data-testid="report-call-issue" onClick={() => setShowCallProblemModal(true)}>
                  <FaFlag /> {showAiNotes ? t('Feedback') : t('Report a call issue')}
                </Button>
              )}
            </ExpandedContent>
          )}
        </div>
      </PatientStatusPanelStyled>
      {audioCallUI && <CallPatient {...callPatientProps} setDeviceModalOpen={(val) => setDevicesModalOpen(val)} />}
      <AudioDevicesModal
        open={devicesModalOpen}
        closeModal={() => setDevicesModalOpen(false)}
        currentInput={currentInput}
        currentOutput={currentOutput}
        updateInputDevice={updateInputDevice}
        updateOutputDevice={updateOutputDevice}
      />
      <ReportCallIssueModal
        open={showCallProblemModal}
        onClose={() => setShowCallProblemModal(false)}
        isVideoSessionType={isVideoSessionType}
        appointment={appointment}
      />
    </div>
  );
};

export default PatientStatusPanel;
